<template>
  <div
    class="flex w-full"
    :data-test-id="
      isSpecialGiftcard ? 'special-giftcard-product-card' : 'product-card'
    "
    :data-kft-list-product="product.objectID"
  >
    <!-- Grid card: the border sits on the text area only, so the photo runs
         to the card's edge. List row: no border at all; the rounded photo and
         the card background carry the shape. -->
    <article
      class="group relative isolate flex w-full overflow-hidden rounded-2xl text-stone-700"
      :class="[toneClass, LAYOUT_CLASSES.article[layout]]"
    >
      <!-- The 4:3 spacer gives the photo its height (and the list row its
           minimum height); the photo itself is absolutely positioned so it
           always fills the box, even when the list row's text runs taller. -->
      <div
        class="relative shrink-0 overflow-hidden bg-stone-100"
        :class="LAYOUT_CLASSES.photo[layout]"
      >
        <div class="aspect-4/3 w-full" aria-hidden="true" />
        <NuxtImg
          v-if="card.image && imageLoading === 'eager'"
          class="product-card-v2-image absolute inset-0 h-full w-full object-cover"
          :src="card.image"
          :alt="card.alt"
          loading="eager"
        />
        <NuxtLazyImg
          v-else-if="card.image"
          class="product-card-v2-image absolute inset-0 h-full w-full object-cover"
          :src="card.image"
          :alt="card.alt"
          loading="lazy"
        />

        <div class="absolute" :class="LAYOUT_CLASSES.badge[layout]">
          <slot name="badge" :badge="card.badge">
            <CardBadge v-if="card.badge" :badge="card.badge" :size="partSize" />
          </slot>
        </div>

        <CardImageChip
          v-if="card.imageChip"
          class="absolute"
          :class="LAYOUT_CLASSES.chip[layout]"
          :chip="card.imageChip"
          display="label"
        />

        <!-- 8px inset so the small heart's 44×44 tap ring isn't clipped -->
        <WishlistButton
          v-if="!disableWishlist"
          class="absolute right-2 top-2 z-10"
          :size="partSize"
          :is-on-wishlist="isOnWishlist"
          @click="emit('click:wishlist')"
        />
      </div>

      <!-- List row: no vertical padding, so the label lines up with the
           photo's top edge and the price with its bottom. A tinted row keeps
           its padding so the text doesn't touch the coloured edge. -->
      <div
        class="flex min-h-0 min-w-0 grow flex-col"
        :class="[
          toneClass,
          LAYOUT_CLASSES.body[layout],
          isTintedBackground && LAYOUT_CLASSES.tintedBody[layout],
        ]"
      >
        <slot name="context" :label="card.contextLabel">
          <CardContextLabel
            :label="card.contextLabel"
            :typography="typography"
          />
        </slot>

        <!-- Weight and leading are repeated with md: because this repo's
             font-size tokens also set weight and line-height, and the
             md:text-* sizes would override them -->
        <h3
          class="m-0 leading-snug text-teal-900 md:leading-snug"
          :class="[
            titleClass,
            typography === 'quiet'
              ? 'font-medium md:font-medium'
              : 'font-semibold md:font-semibold',
          ]"
          data-test-id="kft-product-card-title"
        >
          <!-- Stretched link: its ::after covers the whole card, so the card
               is one click target while the wishlist button stays a sibling
               (a <button> inside an <a> is invalid). -->
          <a
            :href="href"
            :target="openInNewTab ? '_blank' : '_self'"
            class="after:absolute after:inset-0 after:rounded-2xl focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-primary"
            @click="handleClick"
          >
            {{ card.title }}
          </a>
        </h3>

        <slot v-if="showMeta" name="meta" :meta="card.meta">
          <div
            v-if="card.meta.length"
            class="truncate text-caption text-stone-500"
          >
            {{ card.meta.join(' · ') }}
          </div>
        </slot>

        <slot v-if="showWhen" name="when" :when="card.when">
          <div
            v-if="card.when"
            class="truncate text-caption font-semibold text-teal-700"
          >
            {{ card.when }}
          </div>
        </slot>

        <!-- Giftcards have no reviews, and "New on konfetti" would be wrong -->
        <slot v-if="!card.isGiftcard" name="rating" :rating="card.rating">
          <CardRating :rating="card.rating" :size="partSize" />
        </slot>

        <!-- mt-auto pins the price to the bottom so prices line up across a row -->
        <div class="mt-auto">
          <slot name="price" :price="card.price">
            <CardPrice :price="card.price" :size="partSize" />
          </slot>
        </div>
      </div>
    </article>
  </div>
</template>

<script setup lang="ts">
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import { useProductCardLink } from '~/composables/product/useProductCardLink'
import { mapProductCardV2 } from '~/utils/productCardMapper'
import type {
  ProductCardContext,
  ProductCardPartSize,
  ProductCardTone,
  ProductCardTypography,
} from '~/utils/productCardMapper'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'
import { getProductCardToneClass } from '~/utils/productCardTone'
import CardBadge from '~/components/product/card/CardBadge.vue'
import CardContextLabel from '~/components/product/card/CardContextLabel.vue'
import CardImageChip from '~/components/product/card/CardImageChip.vue'
import CardPrice from '~/components/product/card/CardPrice.vue'
import CardRating from '~/components/product/card/CardRating.vue'
import WishlistButton from '~/components/product/card/WishlistButton.vue'

/**
 * - `auto`: list row below Tailwind's `md`, grid card from `md` (listings).
 * - `card`: grid card at every width (rails).
 * - `list`: list row at every width (single-column showcases).
 *
 * Layouts switch in CSS, never in JS, so SSR and hydration render the same
 * markup for every viewport (pages are CDN-cached per URL, not per device).
 */
type ProductCardLayout = 'auto' | 'card' | 'list'
type ProductCardSize = 'default' | 'compact' | 'responsive'

const {
  product,
  layout = 'auto',
  size = 'default',
  context = 'generic',
  tone = 'default',
  typography = 'standard',
  showMeta = false,
  showWhen = false,
  extendedBadges = false,
  isSpecialGiftcard = false,
  isOnWishlist = false,
  disableWishlist = false,
  qid = null,
  openInNewTab = false,
  beforeNavigationCallback = undefined,
  beforeSameTabLocationAssign = undefined,
  imageLoading = 'lazy',
} = defineProps<{
  product: SearchEngineEventItem
  layout?: ProductCardLayout
  /**
   * Grid card only. `compact` is the small mobile-rail card at every width;
   * `responsive` is compact below `md` and default from `md`.
   */
  size?: ProductCardSize
  context?: ProductCardContext
  tone?: ProductCardTone
  typography?: ProductCardTypography
  showMeta?: boolean
  showWhen?: boolean
  extendedBadges?: boolean
  /** The giftcard card ProductShowcase inserts after the first product. */
  isSpecialGiftcard?: boolean
  isOnWishlist?: boolean
  disableWishlist?: boolean
  qid?: string | null
  openInNewTab?: boolean
  beforeNavigationCallback?: () => void
  beforeSameTabLocationAssign?: () => void
  imageLoading?: '' | 'lazy' | 'eager'
}>()

const emit = defineEmits<{ 'click:wishlist': [] }>()

// Literal class strings per layout so Tailwind's JIT picks them up; `auto`
// is the list classes with `md:` overrides for the grid card.
const LAYOUT_CLASSES = {
  article: {
    card: 'flex-col',
    list: 'flex-row',
    auto: 'flex-row md:flex-col',
  },
  photo: {
    card: 'w-full',
    list: 'w-40 rounded-2xl',
    auto: 'w-40 rounded-2xl md:w-full md:rounded-none',
  },
  badge: {
    card: 'left-2.5 top-2.5',
    list: 'left-2 top-2 flex h-7 items-center',
    auto: 'left-2 top-2 flex h-7 items-center md:left-2.5 md:top-2.5 md:block md:h-auto',
  },
  chip: {
    card: 'bottom-2.5 left-2.5',
    list: 'bottom-1.5 left-1.5',
    auto: 'bottom-1.5 left-1.5 md:bottom-2.5 md:left-2.5',
  },
  body: {
    card: 'gap-1.5 rounded-b-2xl border border-t-0 px-3.5 pb-3.5 pt-3',
    list: 'gap-1 pl-3',
    auto: 'gap-1 pl-3 md:gap-1.5 md:rounded-b-2xl md:border md:border-t-0 md:px-3.5 md:pb-3.5 md:pt-3',
  },
  tintedBody: {
    card: '',
    list: 'py-2.5 pr-3',
    auto: 'max-md:py-2.5 max-md:pr-3',
  },
} satisfies Record<string, Record<ProductCardLayout, string>>

const CARD_TITLE_CLASSES: Record<ProductCardSize, string> = {
  default: 'line-clamp-2 text-body',
  compact: 'line-clamp-2 text-label',
  responsive: 'line-clamp-2 text-label md:text-body',
}

const LIST_TITLE_CLASSES = {
  list: 'line-clamp-3 text-label',
  auto: 'line-clamp-3 text-label md:line-clamp-2 md:text-body',
}

/** What a grid card's `size` means for its badge, wishlist, rating, price. */
const CARD_PART_SIZES: Record<ProductCardSize, ProductCardPartSize> = {
  default: 'default',
  compact: 'small',
  responsive: 'responsive',
}

const { t, locale } = useI18n()

const card = computed(() =>
  mapProductCardV2(product, {
    t,
    locale: locale.value,
    localeBCP47: convertLocaleIntoBCP47(locale.value),
    context,
    extendedBadges,
    isSpecialGiftcard,
  })
)

const titleClass = computed(() =>
  layout === 'card' ? CARD_TITLE_CLASSES[size] : LIST_TITLE_CLASSES[layout]
)

const partSize = computed<ProductCardPartSize>(() => {
  if (layout === 'list') return 'small'
  if (layout === 'auto') return 'responsive'
  return CARD_PART_SIZES[size]
})

const toneClass = computed(() => getProductCardToneClass(tone, card.value.kind))

// Only sale / kit / online cards get a coloured background when tinted.
const isTintedBackground = computed(
  () => tone === 'tinted' && card.value.kind !== 'plain'
)

const { href, handleClick } = useProductCardLink({
  link: () => card.value.link,
  qid: () => qid,
  openInNewTab: () => openInNewTab,
  beforeNavigationCallback: () => beforeNavigationCallback,
  beforeSameTabLocationAssign: () => beforeSameTabLocationAssign,
})
</script>

<style lang="postcss" scoped>
/* Same zoom as ProductCard. Hover-capable pointers only: on touch, :hover
   sticks after a tap and the image would stay zoomed. */
.product-card-v2-image {
  @apply transition-transform duration-300 ease-in-out;
}

@media (hover: hover) {
  .group:hover .product-card-v2-image {
    @apply scale-105;
  }
}
</style>
